// src/lib/clock.js — trusted clock + office timezone for the scheduler.
//
// WHY (2026-07-21): attendance and the schedule must NEVER trust the device's wall clock or
// timezone. Two DISTINCT failures were hitting production and are easy to conflate:
//   • wrong TIMEZONE — a device set to another country computed the wrong business day/hour.
//   • wrong CLOCK    — a PC whose time was simply wrong wrote wrong check-in times, corrupting
//                      attendance math. A timezone conversion does NOT fix this: the device's
//                      instant itself is wrong, so we correct it against the SERVER clock, then
//                      format that corrected instant in the office timezone.
// The schedule runs in the OFFICE's timezone (a business fact) — never the agent's device or IP
// (VPNs/roaming make IP unreliable, and an agent abroad still obeys office hours). Today every
// office is Tbilisi; OFFICE_TZ is the single source of truth, ready to become a per-office field
// (office.address → IANA tz) when the agency operates in more than one country.

import { useEffect, useReducer } from 'react'

export const OFFICE_TZ = 'Asia/Tbilisi'   // TODO(multi-office): move to a per-office `timezone` column (office.address → IANA tz)

let _offsetMs = 0        // serverEpoch − deviceEpoch; 0 until syncClock() resolves (device time = fallback)
let _synced   = false
const _listeners = new Set()
function _notify() { for (const fn of _listeners) fn() }

export function clockOffsetMs() { return _offsetMs }
export function clockSkewMs()   { return Math.abs(_offsetMs) }
export function clockSynced()   { return _synced }

// Corrected current instant. A wrong device clock is fixed by the server offset; formatting in
// OFFICE_TZ (below) then fixes a wrong device timezone.
export function now() { return new Date(Date.now() + _offsetMs) }

// Probe the server clock ONCE at app start. The health endpoint returns an ISO `timestamp`; the
// HTTP `Date` header is the fallback. The round trip is halved so network latency doesn't skew
// the offset. Any failure (offline / a content blocker on api.sentavus.com) → device-time
// fallback, _synced stays false, and the skew banner simply doesn't show.
export async function syncClock(apiBase = 'https://api.sentavus.com') {
  try {
    const t0 = Date.now()
    const r = await fetch(`${apiBase}/api/health/deep`, { cache: 'no-store' })
    const t1 = Date.now()
    let serverMs = null
    try { const j = await r.clone().json(); if (j?.timestamp) serverMs = Date.parse(j.timestamp) } catch { /* not json */ }
    if (serverMs == null) { const h = r.headers.get('date'); if (h) serverMs = Date.parse(h) }
    if (serverMs == null || Number.isNaN(serverMs)) return
    _offsetMs = serverMs - (t0 + (t1 - t0) / 2)
    _synced = true
    _notify()
  } catch { /* device-time fallback */ }
}

// ── Office-timezone formatting from a corrected instant ─────────────────────────
// formatToParts reads numeric parts by TYPE, so the locale only affects display, never the
// values. hourCycle h23 forces 00–23 (avoids the '24:00' midnight quirk some engines emit).
function _parts(d, tz) {
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  })
  const p = {}
  for (const part of f.formatToParts(d)) p[part.type] = part.value
  return p
}

export function tzDateStr(d = now(), tz = OFFICE_TZ) { const p = _parts(d, tz); return `${p.year}-${p.month}-${p.day}` }
export function tzTimeStr(d = now(), tz = OFFICE_TZ) { const p = _parts(d, tz); return `${p.hour}:${p.minute}` }
export function tzHour(d = now(), tz = OFFICE_TZ)    { return Number(_parts(d, tz).hour) }
export function tzMinute(d = now(), tz = OFFICE_TZ)  { return Number(_parts(d, tz).minute) }
// Short weekday ('Mon'..'Sun') in the office tz — matches the app's DAY_NAMES / DAYS values.
export function tzDayName(d = now(), tz = OFFICE_TZ) {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(d)
}

// Convenience "current office-time" reads (all corrected + office-tz).
export function todayStr()   { return tzDateStr(now()) }
export function nowDayName() { return tzDayName(now()) }
export function nowHour()    { return tzHour(now()) }

// Live-updating corrected clock for components. Re-renders on the given tick AND when the server
// sync lands (so an initially-uncorrected first paint fixes itself within a frame of sync).
// tickMs = 0 → no interval, only the sync re-render.
export function useClock(tickMs = 30000) {
  const [, force] = useReducer(x => x + 1, 0)
  useEffect(() => {
    _listeners.add(force)
    const id = tickMs ? setInterval(force, tickMs) : null
    return () => { _listeners.delete(force); if (id) clearInterval(id) }
  }, [tickMs])
  return now()
}
